/**
 * Cart discount code: apply via Cart AJAX API, persist in sessionStorage,
 * and carry into checkout (including sidebar / ATC checkout CTAs).
 *
 * Source of truth is cart.js money state — never show an "applied" row unless
 * the cart actually has a positive total_discount / allocated amount.
 *
 * Adapted from obdprice-EU for diagmart sidebar cart (collapsible UI).
 */
(function () {
  var STORAGE_KEY = 'cs_cart_discount_code';

  function rootUrl() {
    var root =
      (window.Shopify && Shopify.routes && Shopify.routes.root) ||
      (window.Theme && Theme.routes && Theme.routes.root_url) ||
      '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  function getStoredCode() {
    try {
      return (sessionStorage.getItem(STORAGE_KEY) || '').trim();
    } catch (e) {
      return '';
    }
  }

  function setStoredCode(code) {
    try {
      if (code) {
        sessionStorage.setItem(STORAGE_KEY, String(code).trim());
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch (e) {
      /* ignore */
    }
  }

  function appendDiscountToUrl(url, code) {
    if (!code || !url) return url;
    try {
      var parsed = new URL(url, window.location.origin);
      if (parsed.pathname.indexOf('/checkout') === -1) return url;
      if (!parsed.searchParams.get('discount')) {
        parsed.searchParams.set('discount', code);
      }
      return parsed.pathname + parsed.search + parsed.hash;
    } catch (e) {
      if (url.indexOf('discount=') !== -1) return url;
      return url + (url.indexOf('?') === -1 ? '?' : '&') + 'discount=' + encodeURIComponent(code);
    }
  }

  function checkoutUrlWithDiscount(code) {
    return appendDiscountToUrl(rootUrl() + 'checkout', (code || getStoredCode() || '').trim());
  }

  function getRenderableDiscountApps(cart) {
    return (cart && cart.cart_level_discount_applications
      ? cart.cart_level_discount_applications
      : []
    ).filter(function (app) {
      if (!app) return false;
      if (app.type && app.type !== 'discount_code') return false;
      return Number(app.total_allocated_amount || 0) > 0;
    });
  }

  function discountMoneyApplied(cart) {
    return Number((cart && cart.total_discount) || 0) > 0;
  }

  /** True only when the code is both present and actually reducing cart money. */
  function cartHasDiscount(cart, code) {
    if (!cart || !code) return false;
    if (!discountMoneyApplied(cart) && !getRenderableDiscountApps(cart).length) {
      return false;
    }

    var needle = String(code).toUpperCase();

    if (Array.isArray(cart.discount_codes)) {
      for (var i = 0; i < cart.discount_codes.length; i++) {
        var entry = cart.discount_codes[i];
        if (
          entry &&
          String(entry.code || '').toUpperCase() === needle &&
          entry.applicable !== false
        ) {
          return discountMoneyApplied(cart) || getRenderableDiscountApps(cart).length > 0;
        }
      }
    }

    var apps = getRenderableDiscountApps(cart);
    for (var j = 0; j < apps.length; j++) {
      if (String(apps[j].title || '').toUpperCase() === needle) return true;
    }

    if (Array.isArray(cart.items)) {
      for (var k = 0; k < cart.items.length; k++) {
        var discounts = cart.items[k].discounts || [];
        for (var m = 0; m < discounts.length; m++) {
          if (
            String(discounts[m].title || '').toUpperCase() === needle &&
            Number(discounts[m].amount || 0) > 0
          ) {
            return true;
          }
        }
      }
    }

    return false;
  }

  function fetchCart() {
    return fetch(rootUrl() + 'cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then(function (res) {
      if (!res.ok) throw new Error('cart.js failed');
      return res.json();
    });
  }

  function discountExplicitlyRejected(cart, code) {
    if (!cart || !code || !Array.isArray(cart.discount_codes)) return false;
    var needle = String(code).toUpperCase();
    for (var i = 0; i < cart.discount_codes.length; i++) {
      var entry = cart.discount_codes[i];
      if (entry && String(entry.code || '').toUpperCase() === needle && entry.applicable === false) {
        return true;
      }
    }
    return false;
  }

  function postDiscount(code) {
    return fetch(rootUrl() + 'cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ discount: code }),
    }).then(function (res) {
      if (!res.ok) throw new Error('cart/update.js failed');
      return res.json();
    });
  }

  function applyDiscount(code) {
    var normalized = (code || '').trim();
    return postDiscount(normalized)
      .then(function () {
        return fetchCart();
      })
      .then(function (cart) {
        if (!normalized) {
          setStoredCode('');
          return cart;
        }

        if (discountExplicitlyRejected(cart, normalized)) {
          var rejected = new Error('invalid_discount');
          rejected.cart = cart;
          throw rejected;
        }

        if (!cartHasDiscount(cart, normalized)) {
          var soft = new Error('checkout_fallback');
          soft.cart = cart;
          soft.soft = true;
          setStoredCode(normalized);
          throw soft;
        }

        setStoredCode(normalized);
        return cart;
      });
  }

  function goToCheckout(code) {
    window.location.href = checkoutUrlWithDiscount(code);
  }

  function bindGlobalCheckoutCarriers() {
    document.addEventListener(
      'submit',
      function (event) {
        var form = event.target;
        if (!(form instanceof HTMLFormElement)) return;

        if (form.querySelector('[data-cart-discount-code]')) return;

        var submitter = event.submitter;
        if (!submitter || submitter.getAttribute('name') !== 'checkout') return;

        var code = getStoredCode();
        if (!code) return;

        event.preventDefault();
        goToCheckout(code);
      },
      true
    );

    document.addEventListener(
      'click',
      function (event) {
        var link =
          event.target && event.target.closest
            ? event.target.closest('a[href*="/checkout"]')
            : null;
        if (link) {
          var code = getStoredCode();
          if (!code) return;
          var next = appendDiscountToUrl(link.getAttribute('href'), code);
          if (next && next !== link.getAttribute('href')) {
            link.setAttribute('href', next);
          }
          return;
        }

        // Sidebar / AJAX cart checkout buttons use onclick → /checkout
        var btn =
          event.target && event.target.closest
            ? event.target.closest('.btn-checkout, [data-cart-checkout]')
            : null;
        if (!btn) return;
        var stored = getStoredCode();
        if (!stored) return;

        var onclick = btn.getAttribute('onclick') || '';
        var match = onclick.match(/['"]([^'"]*\/checkout[^'"]*)['"]/);
        if (match && match[1]) {
          event.preventDefault();
          event.stopPropagation();
          window.location.href = appendDiscountToUrl(match[1], stored);
        }
      },
      true
    );
  }

  function initCartDiscountField() {
    var roots = Array.prototype.slice.call(document.querySelectorAll('[data-cart-discount-code]'));
    if (!roots.length) return;

    var primary = roots[0];
    var moneyFormat = primary.getAttribute('data-money-format') || window.money_format || '';
    var stringsEl = primary.querySelector('[data-cart-discount-strings]');
    var strings = {};

    if (stringsEl) {
      try {
        strings = JSON.parse(stringsEl.textContent || '{}');
      } catch (e) {
        strings = {};
      }
    }

    function t(key, fallback) {
      return strings[key] || fallback || key;
    }

    function formatMoney(cents) {
      if (window.Shopify && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents, moneyFormat || window.money_format);
      }
      return (Number(cents) / 100).toFixed(2);
    }

    function setExpanded(root, expanded) {
      var toggleBtn = root.querySelector('[data-cart-discount-toggle]');
      var panel = root.querySelector('[data-cart-discount-panel]');
      var arrow = root.querySelector('.cart-discount-code__arrow');
      if (!toggleBtn || !panel) return;
      toggleBtn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      panel.hidden = !expanded;
      root.classList.toggle('is-expanded', !!expanded);
      if (arrow) arrow.textContent = expanded ? '▲' : '▼';
    }

    function setMessage(root, text, type) {
      var messageEl = root.querySelector('[data-cart-discount-message]');
      if (!messageEl) return;
      if (!text) {
        messageEl.hidden = true;
        messageEl.textContent = '';
        messageEl.classList.remove('is-success', 'is-error');
        return;
      }
      messageEl.hidden = false;
      messageEl.textContent = text;
      messageEl.classList.toggle('is-success', type === 'success');
      messageEl.classList.toggle('is-error', type === 'error');
    }

    function setBusy(busy) {
      roots.forEach(function (root) {
        var applyBtn = root.querySelector('[data-cart-discount-apply]');
        var input = root.querySelector('[data-cart-discount-input]');
        if (applyBtn) applyBtn.disabled = !!busy;
        if (input) input.disabled = !!busy;
        root.classList.toggle('is-busy', !!busy);
      });
    }

    function getDiscountIconHtml(root) {
      var iconTpl = root.querySelector('[data-cart-discount-icon]');
      return iconTpl ? iconTpl.innerHTML.trim() : '';
    }

    function getCloseIconHtml(root) {
      var iconTpl = root.querySelector('[data-cart-discount-close-icon]');
      return iconTpl ? iconTpl.innerHTML.trim() : '×';
    }

    function renderDiscountList(root, cart) {
      var list = root.querySelector('[data-cart-discounts]');
      if (!list) return;

      var apps = getRenderableDiscountApps(cart);
      if (!apps.length || !discountMoneyApplied(cart)) {
        list.innerHTML = '';
        root.classList.remove('has-applied-discount');
        return;
      }

      var iconHtml = getDiscountIconHtml(root);
      var closeHtml = getCloseIconHtml(root);
      var removeLabel = t('remove', 'Remove discount code');
      var html = '';
      for (var i = 0; i < apps.length; i++) {
        var app = apps[i];
        html +=
          '<li class="discount-list-item cart-discount">' +
          '<span class="cart-discount-title">' +
          iconHtml +
          (app.title || '') +
          '</span>' +
          '<span class="cart-discount-price">-<span class="money">' +
          formatMoney(app.total_allocated_amount || 0) +
          '</span></span>' +
          '<button type="button" class="cart-discount-remove" data-cart-discount-remove aria-label="' +
          removeLabel.replace(/"/g, '&quot;') +
          '">' +
          closeHtml +
          '</button>' +
          '</li>';
      }
      list.innerHTML = html;
      root.classList.add('has-applied-discount');
    }

    function updateDiscountWrapper(scope, cart) {
      if (!scope) return;
      var wrap = scope.querySelector('[data-cart-discount-wrapper]');
      if (!wrap) return;
      var apps = cart.cart_level_discount_applications || [];
      if (apps.length > 0 && Number(apps[0].total_allocated_amount) > 0) {
        wrap.style.display = '';
        var titleEl = wrap.querySelector('[data-cart-discount-title]');
        var amountEl = wrap.querySelector('[data-cart-discount-amount]');
        if (titleEl) titleEl.textContent = apps[0].title || '';
        if (amountEl) amountEl.innerHTML = formatMoney(apps[0].total_allocated_amount);
      } else {
        wrap.style.display = 'none';
      }
    }

    function updateCartSummaries(cart) {
      // Sidebar
      var dropdown = document.getElementById('dropdown-cart');
      if (dropdown) {
        dropdown.querySelectorAll('[data-cart-total], .summary .total .price').forEach(function (el) {
          el.innerHTML = formatMoney(cart.total_price);
        });
        dropdown.querySelectorAll('.summary .original_total_price').forEach(function (el) {
          el.innerHTML = formatMoney(cart.original_total_price);
        });

        var discountRow = dropdown.querySelector('[data-cart-discount-row]');
        var subtotalRow = dropdown.querySelector('[data-cart-subtotal-row]');
        var discountPrice = dropdown.querySelector('[data-cart-discount-row] .discount_price');
        if (Number(cart.total_discount) > 0) {
          if (subtotalRow) subtotalRow.style.display = '';
          if (discountRow) discountRow.style.display = '';
          if (discountPrice) discountPrice.innerHTML = '-' + formatMoney(cart.total_discount);
        } else {
          if (subtotalRow) subtotalRow.style.display = 'none';
          if (discountRow) discountRow.style.display = 'none';
        }
        updateDiscountWrapper(dropdown, cart);
      }

      // Cart page
      var cartTpl = document.querySelector('.cart-template');
      if (cartTpl) {
        cartTpl.querySelectorAll('[data-cart-total], .cs_cart_total .price').forEach(function (el) {
          el.innerHTML = formatMoney(cart.total_price);
        });
        cartTpl.querySelectorAll('[data-cart-subtotal-price], .cs_original_total').forEach(function (el) {
          el.innerHTML = formatMoney(cart.original_total_price);
        });

        var pageDiscountRow = cartTpl.querySelector('[data-cart-page-discount-row]');
        var pageDiscountAmt = cartTpl.querySelector('[data-cart-page-discount-amount]');
        var subtotalBlock = cartTpl.querySelector('[data-cart-subtotal-block]');
        var subtotalPrice = cartTpl.querySelector('[data-cart-subtotal-price]');

        if (Number(cart.total_discount) > 0) {
          if (pageDiscountRow) pageDiscountRow.style.display = '';
          if (pageDiscountAmt) pageDiscountAmt.innerHTML = '-' + formatMoney(cart.total_discount);
          if (subtotalBlock) subtotalBlock.classList.add('cs_cart_subTotal');
          if (subtotalPrice) subtotalPrice.classList.remove('price');
        } else {
          if (pageDiscountRow) pageDiscountRow.style.display = 'none';
          if (subtotalBlock) subtotalBlock.classList.remove('cs_cart_subTotal');
          if (subtotalPrice) subtotalPrice.classList.add('price');
        }
        updateDiscountWrapper(cartTpl, cart);
      }
    }

    function syncFromCart(cart) {
      updateCartSummaries(cart);

      var apps = getRenderableDiscountApps(cart);
      var effective = apps.length > 0 && discountMoneyApplied(cart);
      var codeFromCart = '';

      if (effective) {
        if (Array.isArray(cart.discount_codes)) {
          for (var i = 0; i < cart.discount_codes.length; i++) {
            var entry = cart.discount_codes[i];
            if (entry && entry.applicable !== false && entry.code) {
              codeFromCart = String(entry.code);
              break;
            }
          }
        }
        if (!codeFromCart && apps[0] && apps[0].title) {
          codeFromCart = String(apps[0].title);
        }
        if (codeFromCart) setStoredCode(codeFromCart);
      }

      roots.forEach(function (root) {
        renderDiscountList(root, cart);
        var input = root.querySelector('[data-cart-discount-input]');
        if (effective) {
          if (codeFromCart && input) input.value = codeFromCart;
          setMessage(root, '');
          setExpanded(root, true);
        } else {
          root.classList.remove('has-applied-discount');
        }
      });
    }

    function reconcileCartDiscounts(cart) {
      var apps = getRenderableDiscountApps(cart);
      var codes = cart.discount_codes || [];
      var hasCodes = codes.length > 0;
      var hasRejected = codes.some(function (c) {
        return c && c.applicable === false;
      });
      var moneyOk = discountMoneyApplied(cart);
      var effective = apps.length > 0 && moneyOk;
      var phantom =
        (hasCodes || (cart.cart_level_discount_applications || []).length > 0) && !effective;

      if (phantom || hasRejected) {
        return postDiscount('')
          .then(function () {
            return fetchCart();
          })
          .then(function (fresh) {
            if (hasRejected || phantom) {
              setStoredCode('');
              roots.forEach(function (root) {
                var input = root.querySelector('[data-cart-discount-input]');
                if (input) input.value = '';
              });
            }
            syncFromCart(fresh);
            return fresh;
          })
          .catch(function () {
            setStoredCode('');
            roots.forEach(function (root) {
              var input = root.querySelector('[data-cart-discount-input]');
              if (input) input.value = '';
              var list = root.querySelector('[data-cart-discounts]');
              if (list) list.innerHTML = '';
              root.classList.remove('has-applied-discount');
            });
            syncFromCart(cart);
            return cart;
          });
      }

      syncFromCart(cart);
      return Promise.resolve(cart);
    }

    function getInputCode(root) {
      var input = root.querySelector('[data-cart-discount-input]');
      return (input && input.value ? input.value : '').trim();
    }

    function clearCode() {
      setBusy(true);
      roots.forEach(function (root) {
        setMessage(root, '');
      });

      return applyDiscount('')
        .then(function (cart) {
          roots.forEach(function (root) {
            var input = root.querySelector('[data-cart-discount-input]');
            if (input) input.value = '';
          });
          setStoredCode('');
          syncFromCart(cart);
          return true;
        })
        .catch(function () {
          roots.forEach(function (root) {
            var input = root.querySelector('[data-cart-discount-input]');
            if (input) input.value = '';
          });
          setStoredCode('');
          return fetchCart()
            .then(function (cart) {
              syncFromCart(cart);
            })
            .catch(function () {
              roots.forEach(function (root) {
                var list = root.querySelector('[data-cart-discounts]');
                if (list) list.innerHTML = '';
              });
            });
        })
        .finally(function () {
          setBusy(false);
        });
    }

    function applyCode(root) {
      var code = getInputCode(root);
      if (!code) {
        setMessage(root, t('empty', 'Please enter a discount code'), 'error');
        setExpanded(root, true);
        return Promise.resolve(false);
      }

      setBusy(true);
      setMessage(root, t('applying', 'Applying…'), '');

      return applyDiscount(code)
        .then(function (cart) {
          // Mirror typed code into sibling fields
          roots.forEach(function (r) {
            var input = r.querySelector('[data-cart-discount-input]');
            if (input) input.value = code;
          });
          syncFromCart(cart);
          setMessage(root, t('applied', 'Discount applied'), 'success');
          return true;
        })
        .catch(function (err) {
          if (err && err.message === 'invalid_discount') {
            setStoredCode('');
            setMessage(root, t('invalid', 'Enter a valid discount code'), 'error');
            if (err.cart) syncFromCart(err.cart);
            return false;
          }

          setStoredCode(code);
          if (err && err.cart) syncFromCart(err.cart);
          setMessage(
            root,
            t('checkout_fallback', 'Discount will be applied at checkout'),
            'success'
          );
          return true;
        })
        .finally(function () {
          setBusy(false);
        });
    }

    roots.forEach(function (root) {
      var toggleBtn = root.querySelector('[data-cart-discount-toggle]');
      var applyBtn = root.querySelector('[data-cart-discount-apply]');
      var input = root.querySelector('[data-cart-discount-input]');
      var listEl = root.querySelector('[data-cart-discounts]');

      if (listEl) listEl.innerHTML = '';

      if (toggleBtn) {
        toggleBtn.addEventListener('click', function () {
          var open = toggleBtn.getAttribute('aria-expanded') === 'true';
          setExpanded(root, !open);
        });
      }

      if (applyBtn) {
        applyBtn.addEventListener('click', function () {
          applyCode(root);
        });
      }

      if (input) {
        input.addEventListener('keydown', function (event) {
          if (event.key === 'Enter') {
            event.preventDefault();
            applyCode(root);
          }
        });
      }

      var cartForm = root.closest('form');
      if (cartForm && !cartForm.__csDiscountBound) {
        cartForm.__csDiscountBound = true;
        cartForm.addEventListener(
          'submit',
          function (event) {
            var submitter = event.submitter;
            if (!submitter || submitter.getAttribute('name') !== 'checkout') return;

            var code = getInputCode(root) || getStoredCode();
            if (!code) return;

            event.preventDefault();
            event.stopPropagation();
            setStoredCode(code);

            applyCode(root).finally(function () {
              goToCheckout(code);
            });
          },
          true
        );
      }
    });

    document.addEventListener('click', function (event) {
      var removeBtn =
        event.target && event.target.closest
          ? event.target.closest('[data-cart-discount-remove]')
          : null;
      if (!removeBtn) return;
      var inRoot = roots.some(function (root) {
        return root.contains(removeBtn);
      });
      if (!inRoot) return;
      event.preventDefault();
      clearCode();
    });

    fetchCart()
      .then(reconcileCartDiscounts)
      .catch(function () {
        /* ignore */
      });

    window.addEventListener('cartcount:update', function () {
      fetchCart()
        .then(reconcileCartDiscounts)
        .catch(function () {
          /* ignore */
        });
    });

    if (window.jQuery) {
      jQuery(document).on('ajaxCart.afterCartLoad', function () {
        fetchCart()
          .then(reconcileCartDiscounts)
          .catch(function () {
            /* ignore */
          });
      });
    }
  }

  window.CsCartDiscount = {
    STORAGE_KEY: STORAGE_KEY,
    rootUrl: rootUrl,
    getStoredCode: getStoredCode,
    setStoredCode: setStoredCode,
    appendDiscountToUrl: appendDiscountToUrl,
    checkoutUrlWithDiscount: checkoutUrlWithDiscount,
    cartHasDiscount: cartHasDiscount,
    fetchCart: fetchCart,
    applyDiscount: applyDiscount,
    goToCheckout: goToCheckout,
  };

  function boot() {
    bindGlobalCheckoutCarriers();
    initCartDiscountField();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
